import { useEffect, useState } from "react";
import { coreGet, coreMutate } from "../../core-api/api-client";
import {
  loadCrossWorkspaceDuplicates,
  type CrossWorkspaceDuplicateResponse,
} from "../triage/api";
import { CrossWorkspaceDuplicateNotice } from "../triage/CrossWorkspaceDuplicateNotice";
import "./team-conversion.css";
interface DispatchTeam {
  id: string;
  name: string;
  memberCount: number;
  members: Array<{
    id: string;
    displayName: string;
    occupation: string;
    emailAvailable: boolean;
    emailMasked: string | null;
  }>;
}
export function TeamConversion({
  inquiryId,
  expectedVersion,
  confirmedFields,
  workspaceCode,
  formId,
  renderSubmitButton = true,
  onSubmitStateChange,
  onSuccess,
}: {
  inquiryId: string;
  expectedVersion: number;
  confirmedFields?: Record<string, string>;
  workspaceCode?: string;
  formId?: string;
  renderSubmitButton?: boolean;
  onSubmitStateChange?: (state: {
    inquiryId: string;
    canSubmit: boolean;
    busy: boolean;
  }) => void;
  onSuccess?: (result: { id: string }) => void | Promise<void>;
}) {
  const [teams, setTeams] = useState<DispatchTeam[]>([]),
    [teamId, setTeamId] = useState(""),
    [reason, setReason] = useState("询单已确认，交由团队共同处理"),
    [archiveCc, setArchiveCc] = useState(true),
    [syncEmail, setSyncEmail] = useState(true),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [duplicates, setDuplicates] =
      useState<CrossWorkspaceDuplicateResponse | null>(null),
    [duplicateLoading, setDuplicateLoading] = useState(true),
    [duplicateError, setDuplicateError] = useState(""),
    [acknowledged, setAcknowledged] = useState(false);
  const selectedTeam = teams.find((team) => team.id === teamId);
  const isDevTester = workspaceCode?.trim().toUpperCase() === "DEVTESTER";
  const productFingerprint = `${confirmedFields?.productName ?? ""}\u0000${
    confirmedFields?.cas ?? ""
  }`;
  const requiresDuplicateAcknowledgement = Boolean(
    duplicates?.matches.some(
      ({ distributionStatus }) => distributionStatus === "DISTRIBUTED",
    ),
  );
  const canSubmit = Boolean(
    teamId &&
    reason.trim() &&
    selectedTeam?.memberCount &&
    (!requiresDuplicateAcknowledgement || acknowledged),
  );
  useEffect(() => {
    const controller = new AbortController();
    void coreGet<DispatchTeam[]>("/case-workspace/teams", controller.signal)
      .then((rows) => {
        if (controller.signal.aborted) return;
        const visible = rows.filter((team) => team.memberCount >= 2);
        setTeams(visible);
        setTeamId((current) =>
          visible.some((team) => team.id === current) ? current : "",
        );
      })
      .catch((e) => {
        if (!controller.signal.aborted) setError(e.message);
      });
    return () => controller.abort();
  }, []);
  useEffect(() => {
    const controller = new AbortController();
    setDuplicateLoading(true);
    setDuplicateError("");
    setAcknowledged(false);
    void loadCrossWorkspaceDuplicates(inquiryId, controller.signal)
      .then((result) => {
        if (!controller.signal.aborted) setDuplicates(result);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setDuplicates(null);
        setDuplicateError(
          "跨空间重复检查暂不可用，请分发前人工核对其他工作空间。",
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setDuplicateLoading(false);
      });
    return () => controller.abort();
  }, [inquiryId, productFingerprint]);
  useEffect(() => {
    onSubmitStateChange?.({ inquiryId, canSubmit, busy });
  }, [busy, canSubmit, inquiryId, onSubmitStateChange]);
  async function submit() {
    setBusy(true);
    setError("");
    try {
      const row = await coreMutate<{ id: string }>(
        `/case-workspace/inquiries/${inquiryId}/convert`,
        "POST",
        {
          teamId,
          expectedVersion,
          reason,
          confirmedFields,
          archiveCc: isDevTester ? false : archiveCc,
          syncEmail,
        },
        `team-convert-${crypto.randomUUID()}`,
      );
      await onSuccess?.(row);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <form
      id={formId}
      className="team-conversion"
      role="region"
      aria-label="分发设置"
      onSubmit={(event) => {
        event.preventDefault();
        if (canSubmit && !busy) void submit();
      }}
    >
      <header className="team-conversion-heading">
        <strong>分发设置</strong>
        <p>选择团队后，团队内有效成员都可处理该案件；首期不指定个人负责人。</p>
      </header>
      <CrossWorkspaceDuplicateNotice
        result={duplicates}
        loading={duplicateLoading}
        error={duplicateError}
        acknowledged={acknowledged}
        onAcknowledgedChange={setAcknowledged}
      />
      <div className="team-conversion-fields">
        <label className="team-conversion-field">
          <span>
            处理团队 <small>必选</small>
          </span>
          <select
            aria-label="处理团队"
            value={teamId}
            disabled={busy}
            onChange={(e) => setTeamId(e.target.value)}
          >
            <option value="">请选择团队</option>
            {teams.map((t) => (
              <option value={t.id} key={t.id}>
                {t.name}（{t.memberCount} 人）
              </option>
            ))}
          </select>
        </label>
        {selectedTeam && (
          <section
            className="team-conversion-members"
            aria-label="所选团队成员"
            aria-live="polite"
          >
            <header>
              <strong>{selectedTeam.name}</strong>
              <span className="team-conversion-member-count">
                {selectedTeam.memberCount} 人
              </span>
            </header>
            {selectedTeam.members.length ? (
              <div className="team-conversion-members-scroll">
                <table>
                  <thead>
                    <tr>
                      <th scope="col">成员姓名</th>
                      <th scope="col">职业</th>
                      <th scope="col">联系邮箱</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedTeam.members.map((member) => (
                      <tr key={member.id}>
                        <td>{member.displayName}</td>
                        <td
                          className={
                            member.occupation?.trim()
                              ? undefined
                              : "team-conversion-occupation-empty"
                          }
                        >
                          {member.occupation?.trim() || "未填写职业"}
                        </td>
                        <td
                          className={
                            member.emailAvailable
                              ? undefined
                              : "team-conversion-email-empty"
                          }
                        >
                          {member.emailMasked ?? "未配置，无法投递"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="team-conversion-members-empty">
                该团队暂无有效成员，请确认团队人员配置后再分发。
              </p>
            )}
          </section>
        )}
        <label className="team-conversion-field">
          <span>
            确认依据 <small>必填</small>
          </span>
          <textarea
            rows={3}
            placeholder="请填写确认询单的依据"
            disabled={busy}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </label>
        <label className="team-conversion-archive-option">
          <input
            type="checkbox"
            checked={syncEmail}
            disabled={busy}
            onChange={(event) => setSyncEmail(event.target.checked)}
          />
          <span>
            同步邮件：分发后向所选团队中已配置联系邮箱的每位成员发送内部询单邮件
          </span>
        </label>
        {!isDevTester ? (
          <label className="team-conversion-archive-option">
            <input
              type="checkbox"
              checked={archiveCc}
              disabled={busy}
              onChange={(event) => setArchiveCc(event.target.checked)}
            />
            <span>
              抄送
              sales@benchchem.com（运营归档）：取消勾选后，该案件后续内部通知不再抄送
            </span>
          </label>
        ) : null}
      </div>
      {error && (
        <p className="team-conversion-error" role="alert">
          {error}
        </p>
      )}
      {renderSubmitButton ? (
        <footer className="team-conversion-actions">
          <button
            type="submit"
            className="team-conversion-submit"
            disabled={busy || !canSubmit}
          >
            {busy ? "正在分发…" : "确认客户询单并分发"}
          </button>
        </footer>
      ) : null}
    </form>
  );
}
